import {
  Button,
  Checkbox,
  Descriptions,
  Form,
  Input,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Tabs,
  Typography,
  message,
} from "antd";
import { useEffect, useMemo, useState } from "react";
import api from "../../api/axios";

const { Text } = Typography;

type ShareDetails = {
  saleId: number;
  deliveryMode?: string | null;
  deliveryStatus?: string | null;
  iccid?: string | null;
  activationCode?: string | null;
  smdpServer?: string | null;
  qrCodeUrl?: string | null;
  packageName?: string | null;
  subscriberId?: number | null;
  customerEmail?: string | null;
};

type EmailProvider = "TELTRIP" | "GENERIC";
type ManualMethod = "PRINT" | "MANUAL" | "OTHER";

interface ShareEsimModalProps {
  open: boolean;
  saleId: number | null;
  onClose: () => void;
  onDelivered?: () => void;
}

export function ShareEsimModal({
  open,
  saleId,
  onClose,
  onDelivered,
}: ShareEsimModalProps) {
  const [shareDetails, setShareDetails] = useState<ShareDetails | null>(null);
  const [loadingShare, setLoadingShare] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrBlobUrl, setQrBlobUrl] = useState<string | null>(null);
  const [qrRequested, setQrRequested] = useState(false);
  const [printLoading, setPrintLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("email");
  const [deliveredVia, setDeliveredVia] = useState<string | null>(null);
  const [deliveryStatus, setDeliveryStatus] = useState<string | null>(null);
  const [manualMethod, setManualMethod] = useState<ManualMethod>("PRINT");
  const [form] = Form.useForm();

  const copyToClipboard = async (label: string, value?: string | null) => {
    if (!value) {
      message.warning(`${label} not available`);
      return;
    }

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = value;
        textarea.style.position = "fixed";
        textarea.style.top = "-1000px";
        document.body.appendChild(textarea);
        textarea.focus();
        textarea.select();
        document.execCommand("copy");
        textarea.remove();
      }
      message.success(`${label} copied`);
    } catch {
      message.error(`Failed to copy ${label}`);
    }
  };

  const resetState = () => {
    setShareDetails(null);
    setQrBlobUrl(null);
    setQrRequested(false);
    setLoadingShare(false);
    setEmailLoading(false);
    setManualLoading(false);
    setQrLoading(false);
    setPrintLoading(false);
    setDeliveredVia(null);
    setDeliveryStatus(null);
    setActiveTab("email");
    setManualMethod("PRINT");
    form.resetFields();
  };

  const escapeHtml = (value: string) =>
    value
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const blobToDataUrl = (blob: Blob) =>
    new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(String(reader.result || ""));
      reader.onerror = () => reject(new Error("Failed to read QR blob"));
      reader.readAsDataURL(blob);
    });

  const buildPrintHtml = (qrDataUrl: string, details: ShareDetails | null) => {
    const pkg = escapeHtml(details?.packageName || "-");
    const iccid = escapeHtml(details?.iccid || "-");
    const brand = "Unbranded";
    const lpa = escapeHtml(details?.qrCodeUrl || "-");
    const code = escapeHtml(details?.activationCode || "-");
    const smdp = escapeHtml(details?.smdpServer || "-");

    return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Share eSIM</title>
    <style>
      :root {
        color-scheme: light;
      }
      body {
        font-family: Arial, sans-serif;
        margin: 0;
        padding: 64px 24px 24px;
        color: #111;
        min-height: 100vh;
        box-sizing: border-box;
        display: flex;
        justify-content: center;
      }
      .page {
        max-width: 720px;
        width: 100%;
        margin: 0;
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: baseline;
        margin-bottom: 16px;
      }
      .title {
        font-size: 20px;
        font-weight: 700;
      }
      .meta {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px 24px;
        margin-bottom: 20px;
      }
      .meta-item {
        font-size: 13px;
      }
      .meta-label {
        color: #666;
        display: block;
        margin-bottom: 2px;
        font-size: 11px;
        text-transform: uppercase;
        letter-spacing: 0.04em;
      }
      .qr-wrap {
        display: flex;
        justify-content: center;
        margin: 20px 0;
      }
      .qr {
        width: 240px;
        height: 240px;
        border: 1px solid #ddd;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .qr img {
        max-width: 100%;
        max-height: 100%;
      }
      .detail {
        margin-bottom: 14px;
      }
      .detail label {
        display: block;
        font-size: 12px;
        color: #666;
        margin-bottom: 4px;
      }
      .detail .value {
        font-family: "Courier New", monospace;
        font-size: 12px;
        word-break: break-all;
        padding: 8px;
        border: 1px solid #eee;
        background: #fafafa;
      }
      .actions {
        margin-top: 20px;
      }
      .actions button {
        padding: 8px 16px;
        border-radius: 6px;
        border: 1px solid #111;
        background: #111;
        color: #fff;
        cursor: pointer;
      }
      @media print {
        body {
          margin: 0;
          padding: 64px 24px 24px;
        }
        .actions {
          display: none;
        }
      }
    </style>
  </head>
  <body>
    <div class="page">
      <div class="header">
        <div class="title">Share eSIM</div>
        <div>Brand: ${brand}</div>
      </div>

      <div class="meta">
        <div class="meta-item">
          <span class="meta-label">Package</span>
          <strong>${pkg}</strong>
        </div>
        <div class="meta-item">
          <span class="meta-label">ICCID</span>
          <strong>${iccid}</strong>
        </div>
      </div>

      <div class="qr-wrap">
        <div class="qr">
          <img id="qr" src="${qrDataUrl}" alt="eSIM QR" />
        </div>
      </div>

      <div class="detail">
        <label>eSIM LPA string</label>
        <div class="value">${lpa}</div>
      </div>

      <div class="detail">
        <label>Access code</label>
        <div class="value">${code}</div>
      </div>

      <div class="detail">
        <label>SM-DP+</label>
        <div class="value">${smdp}</div>
      </div>

      <div class="actions">
        <button onclick="window.print()">Print</button>
      </div>
    </div>

    <script>
      (function () {
        var img = document.getElementById('qr');
        var trigger = function () {
          setTimeout(function () {
            window.focus();
            window.print();
          }, 200);
        };
        if (img && img.complete) {
          trigger();
        } else if (img) {
          img.addEventListener('load', trigger);
        }
      })();
    </script>
  </body>
</html>`;
  };

  const fetchShareDetails = async (id: number) => {
    setLoadingShare(true);
    setShareDetails(null);
    setQrBlobUrl(null);
    setQrRequested(false);
    setDeliveredVia(null);
    setDeliveryStatus(null);
    try {
      const res = await api.get(`/pos/Resellers/esim/sales/${id}/share`);
      const details = res.data as ShareDetails;
      setShareDetails(details);
      setDeliveryStatus(details?.deliveryStatus ?? null);
      form.setFieldsValue({
        toEmail: details?.customerEmail || "",
      });
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to load share details.");
    } finally {
      setLoadingShare(false);
    }
  };

  const fetchQr = async (id: number) => {
    setQrRequested(true);
    setQrLoading(true);
    try {
      const res = await api.get(`/pos/Resellers/esim/sales/${id}/qr.png`, {
        responseType: "blob",
      });
      const blobUrl = URL.createObjectURL(res.data);
      setQrBlobUrl(blobUrl);
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to load QR image.");
    } finally {
      setQrLoading(false);
    }
  };

  const handleSendEmail = async () => {
    if (!saleId) return;
    try {
      const values = await form.validateFields();
      setEmailLoading(true);
      const payload = {
        toEmail: values.toEmail,
        subject: values.subject || "",
        message: values.message || "",
        includePdf: false,
        includeUrl: !!values.includeUrl,
        provider: values.provider as EmailProvider,
      };
      const res = await api.post(
        `/pos/Resellers/esim/sales/${saleId}/deliver/email`,
        payload
      );

      message.success("Email sent");
      setDeliveredVia(res.data?.deliveredVia ?? null);
      setDeliveryStatus(res.data?.deliveryStatus ?? null);
      setShareDetails((prev) =>
        prev
          ? { ...prev, deliveryStatus: res.data?.deliveryStatus ?? prev.deliveryStatus }
          : prev
      );
      onDelivered?.();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.displayMessage || "Failed to send email.");
    } finally {
      setEmailLoading(false);
    }
  };

  const handleManualDeliver = async () => {
    if (!saleId) return;
    try {
      setManualLoading(true);
      const res = await api.post(
        `/pos/Resellers/esim/sales/${saleId}/deliver/manual`,
        { method: manualMethod }
      );
      message.success("Marked as delivered");
      setDeliveredVia(res.data?.deliveredVia ?? manualMethod);
      setDeliveryStatus(res.data?.deliveryStatus ?? "DELIVERED");
      setShareDetails((prev) =>
        prev
          ? { ...prev, deliveryStatus: res.data?.deliveryStatus ?? prev.deliveryStatus }
          : prev
      );
      onDelivered?.();
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to mark as delivered.");
    } finally {
      setManualLoading(false);
    }
  };

  const handleDownloadQr = () => {
    if (!qrBlobUrl || !saleId) return;
    const a = document.createElement("a");
    a.href = qrBlobUrl;
    a.download = `esim-${saleId}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrint = async () => {
    if (!saleId) return;
    const printWindow = window.open("", "_blank");
    if (!printWindow) {
      message.error("Popup blocked. Please allow popups to print.");
      return;
    }
    printWindow.opener = null;

    try {
      printWindow.document.open();
      printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Preparing print view...</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        margin: 32px;
        color: #111;
      }
      .card {
        max-width: 520px;
        padding: 24px;
        border: 1px solid #e5e7eb;
        border-radius: 12px;
        background: #fff;
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
      }
      .title {
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 8px;
      }
      .text {
        color: #4b5563;
        font-size: 14px;
        line-height: 1.5;
      }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="title">Preparing print view</div>
      <div class="text">Please wait while we load the QR code.</div>
    </div>
  </body>
</html>`);
      printWindow.document.close();
    } catch {
      // If the browser restricts document access, we will still navigate to the blob URL.
    }

    setPrintLoading(true);
    try {
      const res = await api.get(`/pos/Resellers/esim/sales/${saleId}/qr.png`, {
        responseType: "blob",
      });
      const qrDataUrl = await blobToDataUrl(res.data);
      const html = buildPrintHtml(qrDataUrl, shareDetails);
      const printBlob = new Blob([html], { type: "text/html" });
      const printUrl = URL.createObjectURL(printBlob);

      if (printWindow.closed) {
        URL.revokeObjectURL(printUrl);
        return;
      }

      printWindow.location.href = printUrl;
      const revoke = () => URL.revokeObjectURL(printUrl);
      try {
        printWindow.addEventListener("load", revoke, { once: true });
      } catch {
        setTimeout(revoke, 10000);
      }
    } catch (e: any) {
      message.error(e?.displayMessage || "Failed to open print view.");
      if (!printWindow.closed) {
        try {
          printWindow.document.open();
          printWindow.document.write(`<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <title>Print view failed</title>
    <style>
      body {
        font-family: Arial, sans-serif;
        margin: 32px;
        color: #111;
      }
      .card {
        max-width: 520px;
        padding: 24px;
        border: 1px solid #fecaca;
        border-radius: 12px;
        background: #fff;
      }
      .title {
        font-size: 18px;
        font-weight: 700;
        margin-bottom: 8px;
      }
      .text {
        color: #4b5563;
        font-size: 14px;
        line-height: 1.5;
      }
    </style>
  </head>
  <body>
    <div class="card">
      <div class="title">Could not load the print view</div>
      <div class="text">Please close this tab and try again.</div>
    </div>
  </body>
</html>`);
          printWindow.document.close();
        } catch {
          // If the window is not accessible, do nothing.
        }
      }
    } finally {
      setPrintLoading(false);
    }
  };

  const deliveryLine = useMemo(() => {
    if (deliveredVia) return `Delivered via ${deliveredVia}`;
    if (deliveryStatus) return `Status: ${deliveryStatus}`;
    return "";
  }, [deliveredVia, deliveryStatus]);

  useEffect(() => {
    if (!open) {
      resetState();
      return;
    }

    if (saleId) {
      fetchShareDetails(saleId);
    }
  }, [open, saleId]);

  useEffect(() => {
    if (!open || !saleId) return;
    if (activeTab !== "qr") return;
    if (qrBlobUrl || qrLoading || qrRequested) return;
    fetchQr(saleId);
  }, [open, saleId, activeTab, qrBlobUrl, qrLoading, qrRequested]);

  useEffect(() => {
    return () => {
      if (qrBlobUrl) URL.revokeObjectURL(qrBlobUrl);
    };
  }, [qrBlobUrl]);

  const renderCopyValue = (value?: string | null, label?: string) => {
    if (!value) return <Text type="secondary">-</Text>;
    return (
      <div className="share-esim__copy-field">
        <Text className="share-esim__copy-text" ellipsis={{ tooltip: value }}>
          {value}
        </Text>
        <Button size="small" onClick={() => copyToClipboard(label || "Value", value)}>
          Copy
        </Button>
      </div>
    );
  };

  const tabs = [
    {
      key: "email",
      label: "Share by email",
      children: (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            provider: "TELTRIP",
            includeUrl: true,
            includePdf: false,
          }}
        >
          <Form.Item
            name="toEmail"
            label="To"
            rules={[
              { required: true, message: "Email is required" },
              { type: "email", message: "Enter a valid email" },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>

          <Form.Item
            name="provider"
            label="Email provider"
            rules={[{ required: true, message: "Select a provider" }]}
          >
            <Radio.Group>
              <Radio value="TELTRIP">Teltrip branded</Radio>
              <Radio value="GENERIC">Generic (unbranded)</Radio>
            </Radio.Group>
          </Form.Item>

          <Space style={{ marginBottom: 16 }} wrap>
            <Form.Item name="includeUrl" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>Include LPA URL</Checkbox>
            </Form.Item>
            <Form.Item name="includePdf" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox disabled>Include PDF (coming soon)</Checkbox>
            </Form.Item>
          </Space>

          <Button type="primary" onClick={handleSendEmail} loading={emailLoading}>
            Send email
          </Button>
        </Form>
      ),
    },
    {
      key: "qr",
      label: "QR code",
      children: (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
          <div style={{ width: 220, height: 220, border: "1px solid #f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {qrLoading ? (
              <Spin />
            ) : qrBlobUrl ? (
              <img
                src={qrBlobUrl}
                alt="eSIM QR"
                style={{ width: "100%", height: "100%", objectFit: "contain" }}
              />
            ) : (
              <Text type="secondary">QR not loaded</Text>
            )}
          </div>

          <Space direction="vertical">
            <Button onClick={handleDownloadQr} disabled={!qrBlobUrl}>
              Download PNG
            </Button>
            <Button onClick={() => copyToClipboard("LPA string", shareDetails?.qrCodeUrl)}>
              Copy LPA string
            </Button>
            <Button onClick={() => copyToClipboard("Activation code", shareDetails?.activationCode)}>
              Copy activation code
            </Button>
            <Button onClick={() => copyToClipboard("SM-DP+", shareDetails?.smdpServer)}>
              Copy SM-DP+
            </Button>
          </Space>
        </div>
      ),
    },
    {
      key: "manual",
      label: "Manual",
      children: (
        <Space direction="vertical" size="middle">
          <Select
            value={manualMethod}
            onChange={(v) => setManualMethod(v)}
            style={{ width: 220 }}
            options={[
              { label: "Print", value: "PRINT" },
              { label: "Manual", value: "MANUAL" },
              { label: "Other", value: "OTHER" },
            ]}
          />
          <Space>
            <Button onClick={handlePrint} loading={printLoading} disabled={!saleId}>
              Open print view
            </Button>
            <Button type="primary" onClick={handleManualDeliver} loading={manualLoading}>
              Mark as delivered
            </Button>
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <Modal
      title="Share eSIM"
      open={open}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
    >
      {loadingShare ? (
        <div style={{ padding: "24px 0", textAlign: "center" }}>
          <Spin />
        </div>
      ) : (
        <>
          {deliveryLine && (
            <div style={{ marginBottom: 12 }}>
              <Text type="secondary">{deliveryLine}</Text>
            </div>
          )}

          <Descriptions size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Package">
              {shareDetails?.packageName || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="ICCID">
              {shareDetails?.iccid || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Brand">Unbranded</Descriptions.Item>
          </Descriptions>

          <Descriptions
            size="small"
            column={1}
            layout="vertical"
            className="share-esim__details"
          >
            <Descriptions.Item label="eSIM LPA string">
              {renderCopyValue(shareDetails?.qrCodeUrl, "LPA string")}
            </Descriptions.Item>
            <Descriptions.Item label="Access code">
              {renderCopyValue(shareDetails?.activationCode, "Access code")}
            </Descriptions.Item>
            <Descriptions.Item label="SM-DP+">
              {renderCopyValue(shareDetails?.smdpServer, "SM-DP+")}
            </Descriptions.Item>
          </Descriptions>

          <div style={{ marginTop: 20 }}>
            <Tabs activeKey={activeTab} onChange={setActiveTab} items={tabs} />
          </div>
        </>
      )}
    </Modal>
  );
}

export default ShareEsimModal;
